import React from "react";
import { FileText, FileSpreadsheet, Calculator, Download, Eye, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { SectionHead, downloadDoc } from "./acqUi";

export function Faq({ items }) {
  if (!items?.length) return null;
  return (
    <section data-testid="acq-faq-section">
      <SectionHead eyebrow="Questions" title="Acquisition FAQ" />
      <Accordion type="single" collapsible className="rounded-xl border border-slate-200 bg-white px-4">
        {items.map((f, i) => (
          <AccordionItem key={f.q} value={`f${i}`} className={i === items.length - 1 ? "border-b-0" : ""}>
            <AccordionTrigger className="text-left text-[15px] font-semibold text-slate-900 hover:no-underline" data-testid={`acq-faq-q-${i}`}>{f.q}</AccordionTrigger>
            <AccordionContent className="text-sm text-slate-600 leading-relaxed" data-testid={`acq-faq-a-${i}`}>{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <p className="mt-2 text-xs text-slate-500">Answers summarize the official Program Guidelines, which govern if anything differs.</p>
    </section>
  );
}

function DocCard({ Icon, title, version, text, actions, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 flex flex-col" data-testid={testId}>
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-11 h-11 rounded-lg bg-[#0B2E4F] text-white flex items-center justify-center"><Icon className="w-5 h-5" /></span>
        <div className="min-w-0"><p className="font-semibold text-slate-900 leading-tight">{title}</p>{version && <p className="text-xs text-[#006090] font-medium mt-0.5">{version}</p>}</div>
      </div>
      <p className="text-sm text-slate-600 mt-3 flex-1">{text}</p>
      <div className="mt-4 flex gap-2 flex-wrap">{actions}</div>
    </div>
  );
}

export function Materials({ docs, text, onView, onCalc }) {
  const dl = async (d) => { try { await downloadDoc(d); } catch { toast.error("Download isn't available"); } };
  return (
    <section data-testid="acq-materials-section">
      <SectionHead eyebrow="Official Program Materials" title="Secure Documents" text={text}
        right={<p className="text-xs text-slate-500 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" />Proprietary · access is permissioned</p>} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {docs.map((d) => {
          const pdf = d.mime_type === "application/pdf";
          return (
            <DocCard key={d.id} Icon={pdf ? FileText : FileSpreadsheet} title={d.title} version={d.version_label} text={d.description} testId={`acq-doc-${d.kind}-${d.id}`}
              actions={<>
                {pdf && <Button size="sm" variant="outline" onClick={() => onView(d)} data-testid={`acq-doc-view-${d.id}`}><Eye className="w-4 h-4 mr-1.5" />View</Button>}
                {d.downloadable && <Button size="sm" variant="outline" onClick={() => dl(d)} data-testid={`acq-doc-download-${d.id}`}><Download className="w-4 h-4 mr-1.5" />Download</Button>}
              </>} />
          );
        })}
        <DocCard Icon={Calculator} title="External Acquisition Program Calculator" version="2026" testId="acq-doc-calculator"
          text="In-app estimator built from the bonus schedules and quarterly targets published in the August 2026 Program Guidelines."
          actions={<Button size="sm" variant="outline" onClick={onCalc} data-testid="acq-doc-open-calculator"><Calculator className="w-4 h-4 mr-1.5" />Open Calculator</Button>} />
      </div>
    </section>
  );
}
